import PropTypes from 'prop-types';
import React, {Fragment, useState} from 'react';
import SearchIcon from '@mui/icons-material/Search';
import {
  Autocomplete,
  Divider,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {useDebounce} from 'use-debounce';
import {useUsers} from '@account/hooks/data/users/useUsers';

const UserAutocomplete = ({
  limit = 8,
  onChange,
  placeholder = 'Search for a user by name or email',
  value,
  ...rest
}) => {
  const [inputValue, setInputValue] = useState('');
  const [debouncedSearchTerm] = useDebounce(inputValue.toLowerCase(), 300);

  const {isFetching, data} = useUsers(
    {
      limit,
      search: debouncedSearchTerm ?? '',
    },
    {
      enabled: !!debouncedSearchTerm,
      staleTime: 1000 * 60,
      keepPreviousData: false,
    }
  );

  return (
    <>
      <Autocomplete
        disablePortal
        filterOptions={x => x} // needed because we're filtering on the server
        getOptionLabel={option => option.name}
        getOptionKey={option => option.id}
        // ListboxProps={{sx: {paddingBottom: 0, paddingTop: 0}}}
        loading={isFetching}
        noOptionsText={
          inputValue ? 'No users found' : 'Start typing to search for a user...'
        }
        onChange={(_, value) => onChange(value)}
        onClose={() => setInputValue('')}
        options={data?.results ?? []}
        renderInput={params => (
          <TextField
            {...params}
            data-testid="org-autocomplete-textbox"
            InputProps={{
              ...(params.InputProps ?? {}),
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            placeholder={placeholder}
          />
        )}
        inputValue={inputValue}
        onInputChange={event => setInputValue(event.target.value)}
        renderOption={({key, ...props}, option) => {
          return (
            <Stack
              {...props}
              key={key}
              sx={{
                alignItems: 'start',
                textWrap: 'wrap',
                wordBreak: 'break-word',
              }}
              divider={<Divider />}
            >
              <Typography alignSelf="start">{option.full_name}</Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                alignSelf="start"
                letterSpacing={0}
              >
                {option.email} | {option.memberships?.[0]?.organization.name}
              </Typography>
            </Stack>
          );
        }}
        slotProps={{paper: {elevation: 4}}}
        value={value}
        {...rest}
      />
    </>
  );
};

UserAutocomplete.propTypes = {
  limit: PropTypes.number,
  onChange: PropTypes.func,
  placeholder: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
};

export default UserAutocomplete;
